import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader } from "@/components/ui/card"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { List } from "@/components/ui/list"
import { UserProvider } from "@/components/user-wrapper"
import { toast } from "@/hooks/useToast"
import { formatDateString } from "@/lib/format-date"
import { PdfDocumentViewer } from "@/modules/patients/detail/conclude/document-viewer"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import ReferralLetterDocument from "@/modules/patients/detail/progress-note/documents/referral-letter-document"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { chReferralLetterFragment$key } from "@/modules/patients/detail/referral/ch/services/__generated__/chReferralLetterFragment.graphql"
import { useChResendReferralLetterMutation$data } from "@/modules/patients/detail/referral/ch/services/__generated__/useChResendReferralLetterMutation.graphql"
import { chReferralLetterFragment } from "@/modules/patients/detail/referral/ch/services/chReferralLetterFragment.ts"
import { useChResendReferralLetterMutation } from "@/modules/patients/detail/referral/ch/services/useChResendReferralLetterMutation"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

type SelectedDocument = {
  id: string
  fileId: string | null
  signedAt: string | null
}

type ResendReferralLetterErrorTypename = NonNullable<
  useChResendReferralLetterMutation$data["ch_resendReferralLetter"]["errors"]
>[number]["__typename"]

interface Props {
  progressNote: ProgressNoteData
  referralLetterRef: chReferralLetterFragment$key
  onClose: () => void
}

const ChResendDialog = ({ progressNote, referralLetterRef, onClose }: Props) => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const referralLetter = useFragment(chReferralLetterFragment, referralLetterRef)
  const [resend, isInFlight] = useChResendReferralLetterMutation()

  const parentEpisodeOfCareRef = useEpisodeOfCareStoreContext(
    (state) =>
      state.progressNoteAndParentData.find((item) => item.episodeOfCareProgressNoteData.id === progressNote.id)
        ?.parentEpisodeOfCare ?? null,
  )
  const episodeOfCare = useFragment<EpisodeOfCareFragment$key>(
    getEpisodeOfCareFragment(country),
    parentEpisodeOfCareRef,
  )

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(referralLetter?.id ?? null)
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null)

  const documents: SelectedDocument[] = [
    {
      id: referralLetter.id,
      fileId: referralLetter.fileId,
      signedAt: referralLetter.signedAt,
    },
  ]

  if (documents.length === 0) return null

  const selectedDocument = documents.find((doc) => doc.id === selectedDocumentId) ?? documents[0]

  const handleResend = () => {
    setErrorMessageKey(null)
    resend({
      variables: { input: { referralLetterId: referralLetter.id } },
      onCompleted: (response) => {
        const errorTypename = response.ch_resendReferralLetter.errors?.[0]?.__typename
        const errorMessageKeyByTypename: Partial<Record<ResendReferralLetterErrorTypename, string>> = {
          CareProviderHinEmailMissing: "patient.referralLetter.errors.careProviderHinEmailMissing",
          ReferralLetterNotConcluded: "patient.referralLetter.errors.notConcluded",
          DocumentDoesNotExist: "patient.referralLetter.errors.referralDocumentNotFound",
        }
        const matchedKey = errorTypename ? errorMessageKeyByTypename[errorTypename] : undefined
        if (matchedKey) {
          setErrorMessageKey(matchedKey)
          return
        }
        toast({ description: t("patient.conclude.sendMessageSuccessfully") })
        onClose()
      },
      onError: () => {
        toast({ description: t("error.500.description") })
      },
    })
  }

  const { id: progressNoteId, updatedBy, createdAt, appointment } = progressNote

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="resend-dialog-content"
        className="flex h-[calc(100vh-120px)] flex-col">
        <DialogHeader className="border-b">
          <DialogTitle data-testid="resend-dialog-title">
            {t("patient.header.concludeAndSendDocuments", { count: documents.length })}
          </DialogTitle>
        </DialogHeader>
        <div className="mx-6 flex flex-1 flex-col overflow-hidden py-4">
          <div className="mb-8 grid gap-1">
            {errorMessageKey && (
              <Banner type="inline" variant="critical" data-testid="resend-referral-letter-error-banner">
                {t(errorMessageKey)}
              </Banner>
            )}
            <Banner type="inline" variant="info">
              {t("patient.conclude.reEditWarning")}
            </Banner>
          </div>
          <div className="grid flex-1 grid-cols-[373px_1fr] gap-3 overflow-hidden">
            <ScrollWrapper className="px-1 py-2">
              <Card data-testid="resend-dialog-aside">
                <CardHeader className="gap-4 pl-4">
                  <div className="flex items-center gap-4">
                    <UserProvider
                      userName={updatedBy}
                      render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={progressNoteId} size="md" />}
                    />
                    <div>
                      <div className="text-on-surface grid items-center gap-1 text-base font-medium">
                        <div>
                          {t(`scheduling.waitingRoom.service.${appointment.service}`)}{" "}
                          {t("episodeOfCare.progressNote.for")}
                        </div>
                        <div className="-mt-1">
                          {episodeOfCare
                            ? episodeOfCare.icdDiagnosis.text
                            : t("episodeOfCare.progressNote.unassignedEpisodeOfCare")}
                        </div>
                      </div>
                      <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
                        {formatDateString(createdAt)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="border-t px-4 pb-4">
                  <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
                    {t("patient.conclude.forExternalCareProvider")}
                  </div>
                  <List className="grid gap-2">
                    <ReferralLetterDocument
                      fragmentRef={referralLetterRef}
                      progressNoteId={progressNoteId}
                      isConclusion
                      isSent
                      isSigned={!!referralLetter.signedAt}
                      isSelected={selectedDocumentId === referralLetter.id}
                      onSelect={() => setSelectedDocumentId(referralLetter.id)}
                    />
                  </List>
                </CardBody>
              </Card>
            </ScrollWrapper>
            {selectedDocument?.fileId ? (
              <PdfDocumentViewer fileId={selectedDocument.fileId} signedAt={selectedDocument.signedAt} />
            ) : (
              <div className="flex items-center justify-center">
                <p className="text-on-surface-subdued text-sm">{t("patient.referralLetter.pastDocumentInfoText")}</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-auto flex border-t p-6 pt-4" data-testid="resend-dialog-footer">
          <Button className="px-4 py-2" variant="secondary" onClick={onClose} data-testid="resend-dialog-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <Button
            className="px-4 py-2"
            data-testid="resend-dialog-send-documents-button"
            onClick={handleResend}
            isLoading={isInFlight}>
            {t("patient.conclude.sendDocuments")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { ChResendDialog }
